// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Rail runs a second copy of the app with its own gateway connection,
// so it sees every new message too. Both windows played the notification sound
// (the native banner has one id per message, so it showed once, but the sound
// played twice). The main window holds a Web Lock for as long as it runs; the
// Rail leaves sounds and notifications to it while that lock is held, and only
// notifies itself when the main window is gone (closed without hiding to the tray).
// Both windows share the default session, so they share Web Locks.
// Extra app windows ("New window") ask for the same lock. Only one window holds it at
// a time, so the first window keeps notifying, the others wait in the queue and stay
// quiet, and the next one in line takes over when the first one is closed.

import {isRailWindow} from '@app/features/konvo_rail/utils/RailWindow';

const MAIN_WINDOW_LOCK = 'konvo:main-window';

let holding = false;
let owner = false;

/** Any window but the Rail: hold the lock until this window goes away. */
export function holdMainWindowPresence(): void {
	if (holding || isRailWindow()) return;
	const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
	if (!locks?.request) return;
	holding = true;
	void locks
		.request(MAIN_WINDOW_LOCK, () => {
			owner = true;
			return new Promise<never>(() => {});
		})
		.catch(() => {
			holding = false;
		});
}

/** True in an extra app window while another window holds the lock and owns sounds and notifications. */
export function isWaitingForMainWindow(): boolean {
	return holding && !owner;
}

/** True in the Rail or an extra window while a main window is running, so the main window owns notifications. */
export async function isDeferringToMainWindow(): Promise<boolean> {
	if (!isRailWindow()) return isWaitingForMainWindow();
	const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
	if (!locks?.query) return false;
	try {
		const state = await locks.query();
		return (state.held ?? []).some((lock) => lock.name === MAIN_WINDOW_LOCK);
	} catch {
		return false;
	}
}
